/**
 * Breadcrumb configuration map.
 *
 * Each key is a URL path, and the value describes the human-readable label
 * and optional parent path. The `useBreadcrumbs` hook walks up the `parent`
 * chain to build the full breadcrumb trail for any given location.
 *
 * When adding a new page, add an entry here to have it appear in the
 * breadcrumb navigation automatically.
 *
 */

export type BreadcrumbEntry = {
  label: string
  parent?: string
  isNavigable?: boolean
  hidden?: boolean
  /**
   * Query param names to carry over from the current URL onto this crumb's
   * link. Lets a breadcrumb preserve view state (e.g. the active tab) when
   * the user clicks back to a parent page.
   */
  preserveParams?: string[]
  /**
   * If set, the value of this query param will be appended as a tail crumb
   * (non-navigable) when present in the URL. Useful for showing the selected
   * item (e.g. dataset title) in the breadcrumb without a dedicated route.
   */
  queryParamTail?: string
}

export const breadcrumbConfig: Record<string, BreadcrumbEntry> = {
  "/": {
    label: "Home",
  },

  // Data Sources 
  "/survey-catalogue": {
    label: "Survey Catalogue",
    parent: "/",
  },
  "/articles": {
    label: "Journal Articles",
    parent: "/",
  },
  "/datasets": {
    label: "Datasets",
    parent: "/",
    queryParamTail: "datasetId",
  },
  "/datasets/new": {
    label: "New Dataset",
    parent: "/datasets",
  },
  "/reports": {
    label: "Reports",
    parent: "/",
  },
  // There is no /reports/type page; without this entry the fallback crumb
  // would link to /reports/type, which /reports/:biblioId treats as an id.
  "/reports/type": {
    label: "Type",
    parent: "/reports",
    isNavigable: false,
  },

  // AI Assistants 
  "/ai": {
    label: "AI Assistants",
    parent: "/",
  },
  "/ai/deep-literature": {
    label: "Deep Literature Review",
    parent: "/ai",
  },
  "/ai/exploratory-literature": {
    label: "Exploratory Literature Review",
    parent: "/ai",
  },

  // Data 
  "/data": {
    label: "Data",
    parent: "/",
    hidden: true,
  },
  "/data/parasite-rate": {
    label: "Parasite Rate",
    parent: "/data",
    isNavigable: false,
  },
  "/data/parasite-rate/clusters": {
    label: "PR Clusters",
    parent: "/data/parasite-rate",
  },
  "/data/parasite-rate/polygons": {
    label: "PR Polygons",
    parent: "/data/parasite-rate",
  },
  "/data/rs": {
    label: "Routine Surveillance",
    parent: "/data",
    isNavigable: false,
  },
  "/data/rs/national": {
    label: "National",
    parent: "/data/rs",
  },
  "/data/rs/subnational": {
    label: "Subnational",
    parent: "/data/rs",
  },
  "/data/rs/national/full-page-plot": {
    label: "Full-page Plot",
    parent: "/data/rs/national",
    isNavigable: false,
  },
  "/data/rs/subnational/full-page-plot": {
    label: "Full-page Plot",
    parent: "/data/rs/subnational",
    isNavigable: false,
  },
  "/data/interventions": {
    label: "Interventions",
    parent: "/data",
  },
  "/data/interventions/itn": {
    label: "ITN",
    parent: "/data/interventions",
  },
  "/data/interventions/irs": {
    label: "IRS",
    parent: "/data/interventions",
  },
  "/data/interventions/smc": {
    label: "SMC",
    parent: "/data/interventions",
  },
  "/data/incidence": {
    label: "Incidence",
    parent: "/data",
  },
  "/data/case-management": {
    label: "Case Management",
    parent: "/data",
  },
  "/data/seasonality": {
    label: "Seasonality",
    parent: "/data",
  },
  "/data/covariates": {
    label: "Covariates",
    parent: "/data",
  },

  // Modelling 
  "/modelling": {
    label: "Modelling",
    parent: "/",
    hidden: true,
  },
  "/modelling/pf": {
    label: "Plasmodium falciparum",
    parent: "/modelling",
  },
  "/modelling/global-burden": {
    label: "Global Burden",
    parent: "/modelling",
  },
  "/modelling/more": {
    label: "More",
    parent: "/modelling",
  },

  // Computing 
  "/computing/posit": {
    label: "Posit Workbench",
    parent: "/",
  },
  "/computing/hpc": {
    label: "HPC Jobs",
    parent: "/",
    preserveParams: ["tab", "range"],
  },
  "/computing/hpc/debug": {
    label: "Debug",
    parent: "/computing/hpc",
  },
  "/computing/hpc/logs": {
    label: "Logs",
    parent: "/computing/hpc",
    hidden: true,
  },
  "/computing/hpc/workflow": {
    label: "Workflow",
    parent: "/computing/hpc",
    isNavigable: false,
  },
  "/computing/hpc/array-job": {
    label: "Array Job",
    parent: "/computing/hpc",
    isNavigable: false,
  },
  "/computing/resource-usage": {
    label: "Resource Usage",
    parent: "/",
    isNavigable: false,
  },
  "/computing/resource-usage/disk": {
    label: "Disk Usage",
    parent: "/computing/resource-usage",
  },
  "/computing/resource-usage/token": {
    label: "Token Usage",
    parent: "/computing/resource-usage",
  },
  "/computing/resource-usage/disk/admin": {
    label: "Admin",
    parent: "/computing/resource-usage/disk",
  },
  "/computing/resource-usage/token/admin": {
    label: "Admin",
    parent: "/computing/resource-usage/token",
  },
  "/computing/resource-usage/debug": {
    label: "Debug",
    parent: "/computing/resource-usage/disk",
  },
}
